import { useCallback, useRef, useState, type PointerEvent, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * useTouchLabel — shared long-press / hover label behavior for
 * icon-only buttons that collapse below `lg:`. Mirrors the BrassButton
 * tooltip pattern so in-game header buttons (Trophy, music toggle,
 * settings cog) surface their name on phone long-press too.
 *
 * Usage:
 *   const { handlers, labelNode } = useTouchLabel("Settings");
 *   <button {...handlers} className="group relative ...">
 *     <Icon />
 *     {labelNode}
 *   </button>
 */
export function useTouchLabel(
  label: string,
  opts?: {
    onPointerDown?: (e: PointerEvent<HTMLButtonElement>) => void;
    onPointerUp?: (e: PointerEvent<HTMLButtonElement>) => void;
    onPointerCancel?: (e: PointerEvent<HTMLButtonElement>) => void;
    onPointerLeave?: (e: PointerEvent<HTMLButtonElement>) => void;
    /** When true, label is rendered at all viewports. Defaults to
     *  mobile/tablet only (lg:hidden). */
    alwaysShow?: boolean;
  }
): {
  handlers: {
    onPointerDown: (e: PointerEvent<HTMLButtonElement>) => void;
    onPointerUp: (e: PointerEvent<HTMLButtonElement>) => void;
    onPointerCancel: (e: PointerEvent<HTMLButtonElement>) => void;
    onPointerLeave: (e: PointerEvent<HTMLButtonElement>) => void;
  };
  labelNode: ReactNode;
} {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    clearTimer();
    setVisible(false);
  }, [clearTimer]);

  const schedule = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      setVisible(true);
      timerRef.current = null;
    }, 360);
  }, [clearTimer]);

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      opts?.onPointerDown?.(e);
      if (e.pointerType === "touch" || e.pointerType === "pen") schedule();
    },
    [opts, schedule]
  );
  const onPointerUp = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      opts?.onPointerUp?.(e);
      hide();
    },
    [opts, hide]
  );
  const onPointerCancel = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      opts?.onPointerCancel?.(e);
      hide();
    },
    [opts, hide]
  );
  const onPointerLeave = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      opts?.onPointerLeave?.(e);
      hide();
    },
    [opts, hide]
  );

  const labelNode = (
    <span
      className={cn(
        "pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded-sm border border-border bg-popover px-2 py-1 font-display text-[11px] uppercase tracking-[0.14em] text-popover-foreground shadow-lg transition-opacity duration-150",
        opts?.alwaysShow ? "" : "lg:hidden",
        // Not shown: display none, so the hidden label takes no room (an
        // invisible label past the screen edge still widened the page and
        // let a phone scroll sideways).
        visible ? "opacity-100" : opts?.alwaysShow
          ? "hidden group-hover:block group-focus-visible:block group-hover:opacity-100"
          : "hidden max-lg:group-hover:block max-lg:group-focus-visible:block group-hover:opacity-100"
      )}
    >
      {label}
    </span>
  );

  return {
    handlers: { onPointerDown, onPointerUp, onPointerCancel, onPointerLeave },
    labelNode,
  };
}
